import type { ICommand } from '../../../core/commands/ICommand';
import type { Edge } from '../../../core/models/scene';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import { useDiagramStore } from '../../../stores/diagramStore';
import { validateShapeLimitOrPromptUpgrade } from '../../../utils/shapeLimitUtils';
import { getRelationshipMapping } from '../../../lib/diagram/registries';
import { getProfileForDiagramType } from '../../../core/data-profiles/profileRegistry';
import { resolveEdgeBinding, applyEntityFieldEdgeChange } from '../../data-binding/utils/edgeBinding';
import { getDataBinding } from '../../data-binding/services/BoundShapeEditInterceptor';
import { getGravityUserId } from '../../../hooks/useAuth';
import { markNodeDirty } from '../hooks/useAutoLayout';
import type { IScenarioRecordable, CommandDeltaContribution } from './IScenarioRecordable';
import { createNodeId, createManagerId } from '../models/Scenario';
import { validateOrgReportingRelationship } from '../services/OrgReportingValidationService';
import { collectOrgChartSceneFromDoc } from '../services/orgChartSceneCollect';
import { withAuthoredPlacementCleared } from './authoredPlacement';

export interface CreateReportsToParams {
  id?: string;
  fromId: string;
  toId: string;
  fromAnchor?: 'top' | 'right' | 'bottom' | 'left';
  toAnchor?: 'top' | 'right' | 'bottom' | 'left';
  fromOffset?: number;
  toOffset?: number;
  validFrom?: string;
  validTo?: string;
  dotted?: boolean;
  sourceId?: string;
  userId?: string;
}

export class CreateReportsToCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Create Reporting Line';
  description: string;
  timestamp = Date.now();
  readonly isScenarioRecordable = true as const;

  private params: CreateReportsToParams;
  private createdId?: string;

  constructor(params: CreateReportsToParams) {
    this.id = `create-reports-to-${Date.now()}`;
    this.description = 'Create reporting relationship';
    this.params = params;
  }

  getParams(): CreateReportsToParams {
    return { ...this.params };
  }

  execute(): void {
    const store = useDiagramStore.getState();
    const mutationManager = getNodeMutationManager();
    const doc = mutationManager.getDoc();
    const validationScene = doc ? collectOrgChartSceneFromDoc(doc) : null;

    this.createdId = this.params.id ?? `reports-to-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const isNew = !store.edges.some(e => e.id === this.createdId);
    if (isNew) {
      const validation = validateOrgReportingRelationship({
        fromId: this.params.fromId,
        toId: this.params.toId,
        dotted: this.params.dotted,
        nodes: validationScene?.nodes ?? store.nodes,
        edges: validationScene?.edges ?? store.edges,
      });
      if (!validation.valid) {
        throw new Error(validation.message ?? 'Invalid reporting relationship');
      }
    }
    if (isNew && !validateShapeLimitOrPromptUpgrade(store)) {
      return;
    }

    const relationshipType = this.params.dotted ? 'dotted_line_reports' : 'reports_to';
    const mapping = getRelationshipMapping('org', relationshipType);
    const edgeType = (mapping?.edgeType || 'reporting') as Edge['type'];
    const defaultProps = mapping?.defaultEdgeProps || {};

    const fromAnchor: 'top' | 'right' | 'bottom' | 'left' = this.params.fromAnchor ?? 'top';
    const toAnchor: 'top' | 'right' | 'bottom' | 'left' = this.params.toAnchor ?? 'bottom';

    const newEdge = {
      id: this.createdId,
      type: edgeType,
      from: this.params.fromId,
      to: this.params.toId,
      fromAnchor,
      toAnchor,
      fromOffset: this.params.fromOffset,
      toOffset: this.params.toOffset,
      attrs: {
        ...defaultProps,
        validFrom: this.params.validFrom,
        validTo: this.params.validTo,
        dotted: this.params.dotted,
      },
    };

    mutationManager.addEdge(newEdge);

    // Fresh picker drops stamp authoredPlacement so tree layout won't move them.
    // Once they join the reporting tree, layout is allowed to own their position.
    // Read the store after addEdge so a person created in the same composite is visible.
    for (const nodeId of [this.params.fromId, this.params.toId]) {
      const node = useDiagramStore.getState().nodes.find((candidate) => candidate.id === nodeId);
      const attrs = (node?.attrs ?? {}) as Record<string, unknown>;
      if (attrs.authoredPlacement !== true) continue;
      mutationManager.applyNodeChange(nodeId, { attrs: withAuthoredPlacementCleared(attrs) });
    }

    // Apply entity-field binding: set reportsTo field on the "from" node's bound record
    if (doc) {
      const profile = getProfileForDiagramType('org');
      if (profile) {
        const bindingMapping = resolveEdgeBinding(edgeType, profile);
        if (bindingMapping?.pattern === 'entity-field') {
          const ownerShapeId = bindingMapping.fieldOwner === 'from' ? this.params.fromId : this.params.toId;
          const shapesMap = doc.getMap('shapes');
          const ownerShape = shapesMap.get(ownerShapeId) as Record<string, unknown> | undefined;
          const ownerBinding = ownerShape ? getDataBinding(ownerShape) : null;
          const sourceId = this.params.sourceId ?? ownerBinding?.sourceId;
          const userId = this.params.userId ?? getGravityUserId() ?? 'unknown';
          if (sourceId) {
            applyEntityFieldEdgeChange(doc, bindingMapping, this.params.fromId, this.params.toId, sourceId, userId);
          }
        }
      }
    }

    // Mark the parent node as dirty - it needs to recenter its children
    markNodeDirty(this.params.toId);
  }

  undo(): void {
    if (!this.createdId) return;
    const mutationManager = getNodeMutationManager();

    // Clear entity-field binding: remove the reportsTo field from the "from" node's bound record
    const doc = mutationManager.getDoc();
    if (doc) {
      const profile = getProfileForDiagramType('org');
      if (profile) {
        const bindingMapping = resolveEdgeBinding('reporting', profile);
        if (bindingMapping?.pattern === 'entity-field') {
          const ownerShapeId = bindingMapping.fieldOwner === 'from' ? this.params.fromId : this.params.toId;
          const shapesMap = doc.getMap('shapes');
          const ownerShape = shapesMap.get(ownerShapeId) as Record<string, unknown> | undefined;
          const ownerBinding = ownerShape ? getDataBinding(ownerShape) : null;
          const sourceId = this.params.sourceId ?? ownerBinding?.sourceId;
          const userId = this.params.userId ?? getGravityUserId() ?? 'unknown';
          if (sourceId) {
            applyEntityFieldEdgeChange(doc, bindingMapping, this.params.fromId, null, sourceId, userId);
          }
        }
      }
    }

    mutationManager.deleteEdge(this.createdId);

    // Mark the parent node as dirty - its remaining children need recentering
    markNodeDirty(this.params.toId);
  }

  redo(): void {
    this.execute();
  }

  toScenarioDelta(): CommandDeltaContribution {
    // Creating a reports_to edge is essentially a "move" operation -
    // it establishes who the node reports to.
    // For the scenario delta, we record this as setting the node's manager.
    return {
      moves: [
        {
          nodeId: createNodeId(this.params.fromId),
          oldManagerId: null, // We don't know the old manager here
          newManagerId: createManagerId(this.params.toId),
        },
      ],
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    return {
      moves: [
        {
          nodeId: createNodeId(this.params.fromId),
          oldManagerId: createManagerId(this.params.toId),
          newManagerId: null,
        },
      ],
    };
  }
}
